import { RowStatus, WorkbenchRow } from '../../models/workbench-row.model'

export interface StatusFilterState {
  matched: boolean
  unmatched: boolean
  ignored: boolean
}

/**
 * Which toolbar facet each row status is filtered through.
 *
 * The toolbar offers three tick-boxes, and {@link RowStatus} now carries four members, so the map
 * is explicit rather than an index by status: a `held` row answers to **Unmatched**, consistent with
 * recon, where hold is a sub-state of unmatched. That is what keeps a freshly-held row visible under
 * the filter the user already has on (Unmatched is the only facet ticked by default) instead of
 * vanishing because no facet claims its status.
 *
 * A `Record` over `RowStatus` is the point: a new status without a facet is a compile error here
 * rather than rows silently disappearing from the Prometheus grid.
 */
const STATUS_FACET: Record<RowStatus, keyof StatusFilterState> = {
  matched: 'matched',
  unmatched: 'unmatched',
  ignored: 'ignored',
  held: 'unmatched',
}

export interface QuickFilterState {
  hideFuture: boolean
  postToday: boolean
  cleared: boolean
  uncleared: boolean
}

/** Composite external-filter model assembled from the toolbar controls + any active cross-filter. */
export interface WorkbenchExternalFilterModel {
  crossFilterIds: string[] | null
  status: StatusFilterState | null
  quick: QuickFilterState | null
  fxCurrencies: string[] | null
}

/** "Today" for the CW post-date quick filters, matching the prototype's demo clock. */
export const CW_TODAY = new Date(2026, 6, 1)

const parseCwDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null
  const date = new Date(value as string)
  return isNaN(date.getTime()) ? null : date
}

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/**
 * Whether the status checkboxes narrow anything. Nothing checked means "no status filter" — the same
 * as everything checked — rather than "show nothing", on screen and in the `/query` request alike
 * (see `match-status-filter.ts`).
 */
export const statusFilterConstrains = (status: StatusFilterState): boolean =>
  status.matched || status.unmatched || status.ignored

export const externalFilterActive = (
  model: WorkbenchExternalFilterModel,
): boolean =>
  model.crossFilterIds != null ||
  model.status != null ||
  model.quick != null ||
  (model.fxCurrencies != null && model.fxCurrencies.length > 0)

/** Evaluate the composite external filter for one row. Cross-filter, when present, wins outright. */
export function passesExternalFilter(
  row: WorkbenchRow,
  model: WorkbenchExternalFilterModel,
  statusOf: (row: WorkbenchRow) => RowStatus,
): boolean {
  if (model.crossFilterIds) {
    return model.crossFilterIds.includes(row.rowId)
  }

  if (model.status && statusFilterConstrains(model.status)) {
    if (!model.status[STATUS_FACET[statusOf(row)]]) return false
  }

  if (model.quick) {
    const postDate = parseCwDate(row.fields['postDate'])
    if (model.quick.hideFuture && postDate && postDate > CW_TODAY) return false
    if (model.quick.postToday && !(postDate && sameDay(postDate, CW_TODAY)))
      return false
    const cleared = row.fields['clearedTimestamp'] != null
    if (model.quick.cleared && !cleared) return false
    if (model.quick.uncleared && cleared) return false
  }

  if (model.fxCurrencies && model.fxCurrencies.length) {
    const code = String(row.fields['currencyCode'] ?? '')
    if (!model.fxCurrencies.includes(code)) return false
  }

  return true
}
